/**
 * Analytics utility for Google Analytics 4
 *
 * Only loads and tracks if user has given consent via cookie consent banner.
 * Integrates with consent utility to avoid circular dependencies.
 *
 * BENCHMARKING SETUP:
 * To enable benchmarking in GA4 (https://support.google.com/analytics/answer/13771577):
 * 1. Go to GA4 Admin > Account Settings
 * 2. Enable "Modeling contributions & business insights" setting
 * 3. Set your Industry Category in property settings (helps with peer group matching)
 * 4. Ensure you have minimum volume of users and meaningful data
 * 5. Benchmarking data refreshes every 24 hours and is available starting May 30, 2024
 *
 * This implementation tracks metrics used in benchmarking:
 * - Engagement: engagement time, session duration, engagement rate
 * - Retention: bounce rate, active user ratios
 * - Acquisition: new user rate
 * - Monetization: revenue per user, purchase rates (if ecommerce is implemented)
 */

import { hasAnalyticsConsent } from "@/utils/consent";
import { logger } from "@/utils/logger";
import { env } from "@/config/env";
import {
  trackMetaEvent,
  trackTwitterEvent,
  trackTikTokEvent,
} from "@/utils/marketingPixels";

// GA4 Measurement ID (from environment configuration)
const GA_MEASUREMENT_ID = env.gaMeasurementId;

// Check if GA4 script is loaded
function isGALoaded(): boolean {
  return typeof window !== "undefined" && typeof window.gtag !== "undefined";
}

/**
 * Check if current URL is a production URL
 * Only tracks on www.balpha.co or balpha.co
 */
function isProductionUrl(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  const hostname = window.location.hostname;
  return hostname === "www.balpha.co" || hostname === "balpha.co";
}

/**
 * Initialize Google Analytics 4
 * Only loads if consent is given and GA_MEASUREMENT_ID is set
 */
export function initAnalytics(): void {
  // Don't initialize on non-production URLs
  if (!isProductionUrl()) {
    logger.info("[Analytics] Not a production URL, skipping initialization");
    return;
  }

  // Don't initialize if no measurement ID
  if (!GA_MEASUREMENT_ID) {
    logger.warn("[Analytics] GA4 Measurement ID not configured");
    return;
  }

  // Don't initialize if consent not given
  if (!hasAnalyticsConsent()) {
    logger.info("[Analytics] Consent not given, skipping initialization");
    return;
  }

  // Don't initialize if already loaded
  if (isGALoaded()) {
    return;
  }

  // Initialize dataLayer and gtag function (must be done before loading script)
  // This matches Google's gtag.js implementation
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());

  // Load GA4 script
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Configure GA4 with enhanced measurement for benchmarking
  // Benchmarking requires proper data collection for engagement, retention, and monetization metrics
  window.gtag("config", GA_MEASUREMENT_ID, {
    send_page_view: false, // We'll send page views manually via trackPageView
    // Suppress debug output in production
    debug_mode: import.meta.env.DEV,
    // Note: Enhanced measurement should be enabled in GA4 Admin > Data Streams > Enhanced measurement
    // This helps track engagement metrics used in benchmarking (engagement time, scroll depth, etc.)
    // Industry category and "Modeling contributions & business insights"
    // must be enabled in GA4 Admin > Account Settings for benchmarking to work
  });
}

/**
 * Track page view
 * Enhanced for benchmarking metrics (views per session, views per active user)
 */
export function trackPageView(path: string, title?: string): void {
  if (!isProductionUrl() || !hasAnalyticsConsent() || !isGALoaded()) {
    return;
  }

  if (typeof window.gtag === "function") {
    window.gtag("config", GA_MEASUREMENT_ID, {
      page_path: path,
      page_title: title,
      // Engagement time is automatically tracked by GA4 when enhanced measurement is enabled
    });
  }
}

/**
 * Track custom event
 */
export function trackEvent(
  eventName: string,
  eventParams?: Record<string, unknown>
): void {
  if (!isProductionUrl() || !hasAnalyticsConsent() || !isGALoaded()) {
    return;
  }

  if (typeof window.gtag === "function") {
    window.gtag("event", eventName, eventParams);
  }
}

/**
 * Track CTA click
 */
export function trackCTAClick(ctaText: string, location: string): void {
  trackEvent("cta_click", {
    cta_text: ctaText,
    location,
  });
}

/**
 * Track form submission
 * Tracks form submission attempts and lead conversions across GA4 and marketing pixels
 */
export function trackFormSubmission(formName: string, success: boolean): void {
  // Always track form submission attempt to GA4
  trackEvent("form_submit", {
    form_name: formName,
    success,
  });

  // Track lead conversion only on successful submission
  if (success) {
    // GA4 lead conversion event
    trackEvent("lead", {
      form_name: formName,
      value: 1, // Lead value for conversion tracking
    });

    // Marketing pixel lead events (only if marketing consent given)
    trackMetaEvent("Lead", {
      content_name: formName,
      content_category: "form_submission",
    });

    trackTwitterEvent("Lead", {
      form_name: formName,
    });

    trackTikTokEvent("Lead", {
      form_name: formName,
    });
  }
}

/**
 * Track external link click
 */
export function trackExternalLink(url: string, linkText?: string): void {
  trackEvent("external_link_click", {
    link_url: url,
    link_text: linkText,
  });
}

/**
 * Track user engagement
 * Used for benchmarking engagement metrics like:
 * - Average engagement time per session
 * - Engagement rate
 * - Engaged sessions per active user
 */
export function trackEngagement(engagementTimeMs: number): void {
  trackEvent("user_engagement", {
    engagement_time_msec: engagementTimeMs,
  });
}

/**
 * Track session start
 * Used for benchmarking session metrics
 */
export function trackSessionStart(): void {
  trackEvent("session_start", {
    // GA4 automatically tracks sessions, but this ensures proper attribution
  });
}

/**
 * Track scroll depth for engagement benchmarking
 */
export function trackScrollDepth(depth: number): void {
  trackEvent("scroll", {
    percent_scrolled: depth,
  });
}

/**
 * Track video engagement (if applicable)
 * Used for engagement benchmarking
 */
export function trackVideoEngagement(
  videoTitle: string,
  action: "play" | "pause" | "complete" | "progress",
  progress?: number
): void {
  trackEvent("video_engagement", {
    video_title: videoTitle,
    video_action: action,
    video_progress: progress,
  });
}

/**
 * Track blog article reading completion
 * Should be called when user scrolls through significant portion of article
 */
export function trackBlogArticleRead(
  articleTitle: string,
  articleRoute: string,
  readingTimeMinutes?: number,
  completionPercentage?: number
): void {
  trackEvent("blog_article_read", {
    article_title: articleTitle,
    article_route: articleRoute,
    reading_time_minutes: readingTimeMinutes,
    completion_percentage: completionPercentage,
  });
}

/**
 * Track email link click
 * Wrapper around trackExternalLink for mailto links
 */
export function trackEmailLink(email: string, label?: string): void {
  trackExternalLink(`mailto:${email}`, label || email);
}

// Extend Window interface for gtag
declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}
